import React from 'react';
import {
    Backdrop,
    CircularProgress,
    IconButton,
    Typography,
    useTheme,
} from '@mui/material';
import { ExportIcon } from '../../assets/SvgIcons';

interface ExportButtonProps {
    /** Whether the export is currently loading */
    exportLoading: boolean;
    /** Handler to trigger the export */
    onExport: () => void;
    /** Whether the button should be disabled (e.g., when parent data is loading) */
    disabled?: boolean;
    /** Label to display next to the icon */
    label?: string;
    /** Whether to show a full screen loader overlay during export */
    showFullScreenLoader?: boolean;
    /** Text to show in full screen loader */
    fullScreenLoaderText?: string;
}

const ExportButton: React.FC<ExportButtonProps> = ({
    exportLoading,
    onExport,
    disabled = false,
    label = 'Export',
    showFullScreenLoader = true,
    fullScreenLoaderText = 'Exporting...',
}) => {
    const { palette } = useTheme();

    return (
        <>
            <IconButton
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '5px',
                    alignItems: 'center',
                    padding: '2px 0px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'transparent' },
                }}
                disableFocusRipple={true}
                onClick={onExport}
                disabled={disabled || exportLoading}
            >
                {exportLoading ? (
                    <CircularProgress size={18} />
                ) : (
                    <ExportIcon />
                )}
                <Typography variant="body_400" color={palette.common.black}>
                    {label}
                </Typography>
            </IconButton>

            {/* Full screen loader overlay */}
            {showFullScreenLoader && (
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                    }}
                    open={exportLoading}
                >
                    <CircularProgress color="inherit" />
                    <Typography variant="body_400" color="inherit">
                        {fullScreenLoaderText}
                    </Typography>
                </Backdrop>
            )}
        </>
    );
};

export default ExportButton;
